import { FC, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { CFDFile } from '@aero-platform/shared'
import { useGenerateBlobSasUrlUpload } from '../../api/upload-API'
import { apiClient } from '../../api/configs/axios-setup'
import { addSimChangeMetadata } from '../../api/submission-API'
import { WorkflowSubmission as ProtoWorkflowSubmission } from '@aero-platform/shared'
import { protoService } from '../../proto/ProtoService'
import {
  listBlobsInFolder,
  uploadMultipleFilesWithConcurrency,
  type FileUpload
} from '../../services/blobUploadService'
import simUploadQueueStore from '../../services/simUploadQueueStore'
import { UploadTypes } from '../../utils/constants'
import { currentGMIDState } from '../../utils/state-atoms'
import { WorkflowSubmission } from '../../types/types'
import Modal from '../Modal'
import { Button } from 'primereact/button'
import { InputTextarea } from 'primereact/inputtextarea'
import SimTemplateDropdown from './SimTemplateDropdown'
import { applyOverrideToAllCases } from '../../utils/simTemplateOverride'

type FileData = {
  name: string
  path: string
  rawFile?: File
  abortController?: AbortController
}

type ReplaceSimFileModalProps = {
  submission: WorkflowSubmission
  onClose: () => void
  onComplete: () => void
}

const ReplaceSimFileModal: FC<ReplaceSimFileModalProps> = ({ submission, onClose, onComplete }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Snapshot the submission at modal-open time to prevent stale/mid-update
  // Recoil state from corrupting the protobuf encoding during upload.
  const submissionRef = useRef(submission)
  const [{}, generateSasUrlUpload] = useGenerateBlobSasUrlUpload()
  const gmid = useRecoilValue(currentGMIDState)
  const [selectedFile, setSelectedFile] = useState<FileData | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<CFDFile | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [verifying, setVerifying] = useState(false)
  const [reason, setReason] = useState('')
  const [reasonTouched, setReasonTouched] = useState(false)

  const isReasonValid = reason.trim().length > 0
  const hasSelection = selectedFile !== null || selectedTemplate !== null

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      const file = files[0]
      setSelectedFile({
        name: file.name,
        path: file.name,
        rawFile: file,
        abortController: new AbortController()
      })
      setSelectedTemplate(null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleUpload = async () => {
    if (!hasSelection) {
      toast.error('Please select a SIM template or upload a file.')
      return
    }

    setReasonTouched(true)
    if (!isReasonValid) {
      toast.error('A reason is required for the SIM file change.')
      return
    }

    setSubmitting(true)

    try {
      const sub = submissionRef.current
      const previousSimFile = sub.runConfig?.overrideSelectedSimFile || ''

      if (selectedFile?.rawFile) {
        // Upload flow — upload file to blob storage then update submission.
        // Block until the browser upload actually finishes, finalize the server-side copy, and
        // confirm the blob is present in Azure before reporting success — so the run can never
        // be updated to a sim that never landed.
        const aeroTempRunId = `${Date.now()}`
        const blobPrefix = `${gmid}/${sub.name}`

        const filesToUpload: FileUpload[] = [
          {
            file: selectedFile.rawFile,
            blobName: `${blobPrefix}/${selectedFile.path}`,
            abortController: selectedFile.abortController
          }
        ]

        setUploadProgress(0)
        const uploadResults = await uploadMultipleFilesWithConcurrency(
          generateSasUrlUpload,
          UploadTypes.OVERRIDE_STARCCM_TEMPLATE_FILE,
          filesToUpload,
          aeroTempRunId,
          gmid,
          (_filePath, percent) => setUploadProgress(percent)
        )
        if (!uploadResults.some(r => r.success)) {
          throw new Error('The SIM file failed to upload to Azure.')
        }

        const plainSub = (sub as any).toObject ? (sub as any).toObject() : sub
        const simOverrideDir = `/staging_overrides/${gmid}/${sub.name}`
        // Run-level replace: stamp the override template onto EVERY case so config.json's
        // per-case template path reflects the new sim (and is never emitted empty — StarCCM
        // exit code 36). The server resolves the relative path to a full blob URL on save.
        const overrideTemplate = {
          file: selectedFile.name,
          path: simOverrideDir,
          system: 'starccm',
          fileType: 'template',
          rootType: 'override'
        }
        const selectedCasesWithTemplate = applyOverrideToAllCases(
          plainSub.runConfig?.selectedCases || [],
          overrideTemplate
        )
        const mutableSub = ProtoWorkflowSubmission.fromObject({
          ...plainSub,
          runConfig: {
            ...plainSub.runConfig,
            overrideSelectedSimFile: selectedFile.name,
            simOverrideDir,
            selectedCases: selectedCasesWithTemplate
          }
        })

        const dataToPost = protoService.encodePayload(mutableSub, 'workflowsubmission')
        if (!dataToPost?.data_base64) {
          throw new Error('Failed to encode submission data — payload was empty.')
        }
        await apiClient.put(`/WorkflowSubmissions/${sub._id}`, {
          data_base64: dataToPost.data_base64,
          aeroTempRunId,
          gmid,
          skipSimFileCopy: true
        })

        // Finalize: server-side temp → permanent blob copy (fast, Azure-internal).
        setVerifying(true)
        await apiClient.post(`/WorkflowSubmissions/${sub._id}/finalize-sim-upload`, {
          aeroTempRunId,
          gmid
        })

        // Confirm the blob actually landed in the permanent staging_overrides location.
        const confirmLanded = async () => {
          const landedFiles = await listBlobsInFolder(
            generateSasUrlUpload,
            UploadTypes.OVERRIDE_STARCCM_TEMPLATE_FILE,
            `${gmid}/${sub.name}`,
            null,
            gmid
          )
          return landedFiles.includes(selectedFile.name)
        }
        let landed = await confirmLanded()
        if (!landed) {
          // Guard against a brief blob-list propagation delay: retry once before failing.
          await new Promise(resolve => setTimeout(resolve, 1500))
          landed = await confirmLanded()
        }
        if (!landed) {
          throw new Error(
            'Upload finished but the SIM file could not be confirmed in Azure. Please re-upload.'
          )
        }

        // Persist the SIM change reason
        await addSimChangeMetadata(sub._id, {
          caseName: sub.name,
          previousSimFile,
          newSimFile: selectedFile.name,
          reason: reason.trim()
        }).catch(err => console.warn('Failed to persist SIM change metadata:', err))

        toast.success(`SIM file "${selectedFile.name}" confirmed in Azure.`)
      } else if (selectedTemplate) {
        // Dropdown selection flow — update the submission with the selected template reference
        const plainSub = (sub as any).toObject ? (sub as any).toObject() : sub

        // selectedTemplate.path from the server is a full blob URL
        // (e.g. https://{account}.blob.core.windows.net/cfd-cases/{team}/staging_overrides/…/).
        // simOverrideDir must be only the path segment within /cfd-cases/{raceTeamId}, so strip the prefix.
        let simOverrideDir = selectedTemplate.path || `/staging_overrides/${gmid}/${sub.name}`
        if (simOverrideDir.startsWith('https://')) {
          try {
            const url = new URL(simOverrideDir)
            const containerTeamPrefix = `/cfd-cases/${sub.raceTeamId}`
            if (url.pathname.startsWith(containerTeamPrefix)) {
              simOverrideDir = url.pathname.slice(containerTeamPrefix.length).replace(/\/$/, '')
            }
          } catch {
            // fall back to the raw value if URL parsing fails
          }
        } else {
          simOverrideDir = simOverrideDir.replace(/\/$/, '')
        }

        // Run-level replace: stamp the selected template onto EVERY case so config.json's
        // per-case template path reflects the new sim (and is never emitted empty — StarCCM
        // exit code 36).
        const overrideTemplate = {
          file: selectedTemplate.file,
          path: selectedTemplate.path || simOverrideDir,
          system: selectedTemplate.system || 'starccm',
          fileType: selectedTemplate.fileType || 'template',
          rootType: selectedTemplate.rootType || 'override'
        }
        const selectedCasesWithTemplate = applyOverrideToAllCases(
          plainSub.runConfig?.selectedCases || [],
          overrideTemplate
        )
        const mutableSub = ProtoWorkflowSubmission.fromObject({
          ...plainSub,
          runConfig: {
            ...plainSub.runConfig,
            overrideSelectedSimFile: selectedTemplate.file,
            simOverrideDir,
            selectedCases: selectedCasesWithTemplate
          }
        })

        const dataToPost = protoService.encodePayload(mutableSub, 'workflowsubmission')
        if (!dataToPost?.data_base64) {
          throw new Error('Failed to encode submission data — payload was empty.')
        }
        await apiClient.put(`/WorkflowSubmissions/${sub._id}`, {
          data_base64: dataToPost.data_base64,
          gmid,
          skipSimFileCopy: true
        })

        // Persist the SIM change reason
        await addSimChangeMetadata(sub._id, {
          caseName: sub.name,
          previousSimFile,
          newSimFile: selectedTemplate.file,
          reason: reason.trim()
        }).catch(err => console.warn('Failed to persist SIM change metadata:', err))

        toast.success(`SIM template updated to "${selectedTemplate.file}".`)
      }

      onComplete()
    } catch (err) {
      console.error('Error replacing SIM file:', err)
      toast.error(err instanceof Error && err.message ? err.message : 'Error replacing SIM file.')

      // Abort the in-flight blob upload and remove the progress bar entry
      if (selectedFile?.abortController) {
        selectedFile.abortController.abort()
        simUploadQueueStore.dismiss(submissionRef.current._id)
      }

      setVerifying(false)
      setUploadProgress(0)
      setSubmitting(false)
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
  }

  return (
    <Modal
      title="Replace SIM File"
      isOpen={true}
      withAction={false}
      size="md"
      easyClose={!submitting}
      onClose={onClose}
      onCancel={submitting ? undefined : onClose}>
      <div className="flex flex-col gap-4 pt-2">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Select an existing SIM template or upload a new file to replace the current template for
          this run.
        </p>
        {submission.runConfig.overrideSelectedSimFile && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Current:{' '}
            <span className="font-medium">{submission.runConfig.overrideSelectedSimFile}</span>
          </p>
        )}

        {/* SIM Template Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-neutral-10 mb-2">
            SIM Template
          </label>
          <SimTemplateDropdown
            submissionId={submission._id}
            submissionName={submission.name}
            value={selectedTemplate}
            onChange={val => {
              setSelectedTemplate(val)
              if (val) setSelectedFile(null)
            }}
            placeholder="Select a template..."
            disabled={submitting}
          />
          <div className="text-sm text-gray-500 mt-1">
            {selectedTemplate
              ? `(${selectedTemplate.rootType}-${
                  selectedTemplate.fileType || selectedTemplate.rootType || selectedTemplate.system
                }) ${selectedTemplate.file}`
              : 'None'}
          </div>
        </div>

        {/* OR divider */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-gray-300 dark:border-neutral-60" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white dark:bg-neutral-90 text-gray-500 dark:text-neutral-20">
              OR
            </span>
          </div>
        </div>

        {/* File upload */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-neutral-10 mb-2">
            Upload New SIM File
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".sim,.java"
            onChange={handleFileSelection}
            className="hidden"
          />

          {!selectedFile ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
              className="flex items-center justify-center gap-2 w-full p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded text-sm text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              <i className="pi pi-upload" />
              Click to select a SIM file
            </button>
          ) : (
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-600">
              <i className="pi pi-file text-blue-500 text-sm" />
              <span className="flex-1 text-sm truncate" title={selectedFile.name}>
                {selectedFile.name}
              </span>
              {!submitting && (
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove">
                  <i className="pi pi-times text-xs" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Required reason for SIM file change */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason for SIM file change <span className="text-red-500">*</span>
          </label>
          <InputTextarea
            value={reason}
            onChange={e => {
              setReason(e.target.value)
              if (!reasonTouched) setReasonTouched(true)
            }}
            rows={3}
            placeholder="Describe why the SIM file is being replaced..."
            className={reasonTouched && !isReasonValid ? 'p-invalid' : ''}
            disabled={submitting}
          />
          {reasonTouched && !isReasonValid && (
            <small className="text-red-500">A reason is required to replace the SIM file.</small>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            label="Cancel"
            className="p-button-text p-button-sm"
            onClick={onClose}
            disabled={submitting}
          />
          <Button
            label={
              verifying
                ? 'Confirming in Azure…'
                : submitting
                  ? selectedFile?.rawFile
                    ? `Uploading… ${uploadProgress}%`
                    : 'Saving…'
                  : 'Upload'
            }
            icon={submitting ? 'pi pi-spin pi-spinner pr-1' : 'pi pi-upload pr-1'}
            className="p-button-sm disabled:!bg-gray-300 disabled:!border-gray-300 disabled:!text-gray-500 dark:disabled:!bg-neutral-70 dark:disabled:!text-gray-400 disabled:!cursor-not-allowed disabled:!opacity-100"
            onClick={handleUpload}
            disabled={!hasSelection || submitting || !isReasonValid}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ReplaceSimFileModal
